// Renders a page's icon. Notion (and our migration) supports four icon types:
//   - Emoji (e.g. "🛠️") — render as text
//   - Wiki-hosted URL (e.g. "/media/<id>/icon-<v>.png") — render as <img>
//   - External URL (e.g. "https://...") — render as <img>
//   - Data URL (e.g. "data:image/png;base64,...") — Notion returns these for some
//     small inline icons that were uploaded as base64 — render as <img>
//
// We detect by checking the prefix. Anything else is assumed to be emoji/text.
//
// Use this component everywhere a page icon is shown so the rendering is consistent
// across the page header, breadcrumbs, and the Sub-pages list.

interface PageIconProps {
  icon: string | null | undefined;
  /** Tailwind size class for image icons (e.g. "w-5 h-5"). Defaults to small. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Optional alt text for image icons */
  alt?: string;
  /** Whether to show a placeholder dot when icon is missing */
  fallback?: 'dot' | 'none';
  /** Extra classes for the wrapping element */
  className?: string;
}

const SIZE_CLASSES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-8 h-8',
  xl: 'w-16 h-16',
};

export function PageIcon({
  icon,
  size = 'sm',
  alt = '',
  fallback = 'none',
  className = '',
}: PageIconProps) {
  if (!icon) {
    if (fallback === 'dot') return <span className={`text-brand-300 ${className}`}>·</span>;
    return null;
  }

  const isUrl =
    icon.startsWith('/') || icon.startsWith('http') || icon.startsWith('data:');

  if (isUrl) {
    return (
      <img
        src={icon}
        alt={alt}
        className={`${SIZE_CLASSES[size]} object-contain shrink-0 inline-block align-middle ${className}`}
      />
    );
  }

  // Emoji or text — render as inline text
  return <span className={`shrink-0 ${className}`}>{icon}</span>;
}
